/**
 * Income: list (with totals and export), add/edit form with proof upload, detail.
 */
import { INCOME_TYPES, PAYMENT_METHODS } from "@shared/constants.ts";
import { incomeSchema, periodOrder } from "@shared/schemas/finance.ts";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { AttachOnSave, uploadAll } from "../../components/forms/AttachOnSave.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  MoneyField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { type Column, DataTable, Pagination } from "../../components/ui/DataTable.tsx";
import { DateRangePicker } from "../../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect, SearchInput } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useDateRange, useSortState, useUrlState } from "../../lib/url-state.ts";

export interface IncomeRow {
  id: string;
  propertyId: string;
  tenancyId: string | null;
  incomeType: string;
  amountPence: number;
  receivedDate: string;
  periodFromDate: string | null;
  periodToDate: string | null;
  description: string;
  reference: string;
  paymentMethod: string | null;
  notes: string;
  addressLine1: string;
  postcode: string;
  tenantNames: string | null;
  documentCount: number;
}

interface Paged<T> {
  items: T[];
  total: number;
  totalPence: number;
  page: number;
  pageSize: number;
}

/** Income table used by the income page and property/tenancy tabs. */
export function IncomeTable({ filters, showProperty = true }: { filters: Params; showProperty?: boolean }) {
  const f = useFormat();
  const { sort, onSort, page, setPage } = useSortState("date", "desc");
  const q = useApi<Paged<IncomeRow>>(
    "/api/income",
    { ...filters, sort: sort.sort, dir: sort.dir, page },
    { keepPrevious: true },
  );
  const columns: Column<IncomeRow>[] = [
    { key: "date", header: "Received", sort: "date", cell: (r) => f.date(r.receivedDate) },
    ...(showProperty
      ? [
          {
            key: "property",
            header: "Property",
            sort: "property",
            cell: (r: IncomeRow) => r.addressLine1,
          } satisfies Column<IncomeRow>,
        ]
      : []),
    { key: "type", header: "Type", sort: "type", cell: (r) => f.label(INCOME_TYPES, r.incomeType) },
    {
      key: "from",
      header: "From / reference",
      cell: (r) => [r.tenantNames, r.description, r.reference].filter(Boolean).join(" · ") || "—",
    },
    {
      key: "docs",
      header: <Icon name="paperclip" size={14} />,
      cell: (r) => (r.documentCount ? r.documentCount : ""),
    },
    {
      key: "amount",
      header: "Amount",
      sort: "amount",
      align: "end",
      cell: (r) => <Money pence={r.amountPence} kind="income" />,
    },
  ];
  return (
    <QueryState query={q}>
      {(data) => (
        <>
          <DataTable
            rows={data.items}
            columns={columns}
            rowKey={(r) => r.id}
            rowLink={(r) => `/income/${r.id}`}
            sort={sort}
            onSort={onSort}
            empty={
              <EmptyState
                icon="income"
                title="No income recorded"
                action={
                  <IconButtonLink
                    to={withQuery("/income/new", {
                      propertyId: filters.propertyId as string,
                      tenancyId: filters.tenancyId as string,
                    })}
                    icon="plus"
                  >
                    Record income
                  </IconButtonLink>
                }
              >
                Record rent and other money received. Totals, reports and tax figures update automatically.
              </EmptyState>
            }
            footer={
              <tr className="fw-semibold">
                <td colSpan={columns.length - 1}>Total ({data.total} records)</td>
                <td className="text-end">
                  <Money pence={data.totalPence} kind="income" />
                </td>
              </tr>
            }
          />
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
        </>
      )}
    </QueryState>
  );
}

export function IncomeListPage() {
  const range = useDateRange();
  const { get } = useUrlState();
  const lookups = useLookups();
  const filters: Params = {
    from: range.from,
    to: range.to,
    propertyId: get("propertyId"),
    incomeType: get("incomeType"),
    q: get("q"),
  };
  return (
    <>
      <PageHeader
        title="Income"
        subtitle="Rent and other money received"
        actions={
          <>
            <Can permission="income:export">
              <a className="btn btn-outline-secondary" href={withQuery("/api/income/export.csv", filters)}>
                <Icon name="download" size={16} /> Export CSV
              </a>
            </Can>
            <Can permission="income:write">
              <IconButtonLink to="/income/new" icon="plus">
                Record income
              </IconButtonLink>
            </Can>
          </>
        }
      />
      <Card>
        <FilterBar>
          <DateRangePicker range={range} />
          <SearchInput placeholder="Search description or reference" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect param="incomeType" label="Type" options={INCOME_TYPES} />
        </FilterBar>
        <IncomeTable filters={filters} />
      </Card>
    </>
  );
}

function TenancyAutofill() {
  const { watch, setValue, getValues } = useFormContext();
  const lookups = useLookups();
  const propertyId = watch("propertyId");
  const tenancyId = watch("tenancyId");
  // Pick the property's current tenancy automatically.
  useEffect(() => {
    if (!propertyId || getValues("tenancyId")) return;
    const current = lookups?.tenancies.filter(
      (t) => t.propertyId === propertyId && (t.status === "active" || t.status === "periodic"),
    );
    if (current?.length === 1) setValue("tenancyId", current[0]!.id);
  }, [propertyId, lookups, getValues, setValue]);
  // Pre-fill the amount with the current rent.
  const tenancy = useApi<{ currentRent: { amountPence: number } | null }>(
    tenancyId ? `/api/tenancies/${tenancyId}` : null,
  );
  useEffect(() => {
    const rent = tenancy.data?.currentRent?.amountPence;
    if (rent && !getValues("amountPence") && getValues("incomeType") === "rent")
      setValue("amountPence", rent);
  }, [tenancy.data, getValues, setValue]);
  return null;
}

export function IncomeFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  const [files, setFiles] = useState<File[]>([]);
  return (
    <EntityForm
      schema={incomeSchema}
      checks={[periodOrder]}
      resource="/api/income"
      id={id}
      title={id ? "Edit income" : "Record income"}
      breadcrumbs={[{ label: "Income", to: "/income" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        tenancyId: params.get("tenancyId") ?? "",
        incomeType: "rent",
        amountPence: "",
        receivedDate: new Date().toISOString().slice(0, 10),
        periodFromDate: "",
        periodToDate: "",
        description: "",
        reference: "",
        paymentMethod: "bank_transfer",
        notes: "",
      }}
      toForm={(r) => ({
        ...r,
        tenancyId: r.tenancyId ?? "",
        paymentMethod: r.paymentMethod ?? "",
        periodFromDate: r.periodFromDate ?? "",
        periodToDate: r.periodToDate ?? "",
      })}
      afterSave={async (saved) => uploadAll(files, "income", saved.id as string, "statement")}
      redirect={(saved) => `/income/${saved.id}`}
      cancelTo={id ? `/income/${id}` : "/income"}
    >
      {(form) => {
        const propertyId = form.watch("propertyId");
        const tenancyOptions = toOptions(lookups?.tenancies, (t) => t.propertyId === propertyId);
        return (
          <>
            {!id && <TenancyAutofill />}
            <Row>
              <Col md={6}>
                <SelectField
                  name="propertyId"
                  label="Property"
                  options={toOptions(lookups?.properties)}
                  placeholder="Choose a property"
                  required
                />
              </Col>
              <Col md={6}>
                <SelectField
                  name="tenancyId"
                  label="Tenancy"
                  options={tenancyOptions}
                  placeholder="Not linked to a tenancy"
                  hint="Link rent to a tenancy to track arrears."
                />
              </Col>
              <Col md={4}>
                <SelectField name="incomeType" label="Income type" options={INCOME_TYPES} required />
              </Col>
              <Col md={4}>
                <MoneyField name="amountPence" label="Amount received" required />
              </Col>
              <Col md={4}>
                <DateField name="receivedDate" label="Date received" required />
              </Col>
              <Col md={4}>
                <SelectField
                  name="paymentMethod"
                  label="Payment method"
                  options={PAYMENT_METHODS}
                  placeholder="—"
                />
              </Col>
              <Col md={4}>
                <DateField
                  name="periodFromDate"
                  label="Covers period from"
                  hint="Optional — used for accruals basis."
                />
              </Col>
              <Col md={4}>
                <DateField name="periodToDate" label="Covers period to" />
              </Col>
              <Col md={6}>
                <TextField name="description" label="Description" placeholder="e.g. Rent for March" />
              </Col>
              <Col md={6}>
                <TextField name="reference" label="Reference" placeholder="Bank reference" />
              </Col>
              <Col md={12}>
                <TextAreaField name="notes" label="Notes" rows={2} />
              </Col>
              {!id && (
                <Col md={12}>
                  <AttachOnSave
                    files={files}
                    onChange={setFiles}
                    label="Upload proof (optional)"
                    hint="Bank statement, payment confirmation or receipt."
                  />
                </Col>
              )}
            </Row>
          </>
        );
      }}
    </EntityForm>
  );
}

export function IncomeDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<IncomeRow>(`/api/income/${id}`);
  return (
    <QueryState query={q}>
      {(r) => (
        <>
          <PageHeader
            title={
              <>
                <Money pence={r.amountPence} kind="income" />{" "}
                {f.label(INCOME_TYPES, r.incomeType).toLowerCase()}
              </>
            }
            subtitle={`Received ${f.date(r.receivedDate)}`}
            breadcrumbs={[{ label: "Income", to: "/income" }, { label: "Transaction" }]}
            actions={
              <>
                <Can permission="income:write">
                  <IconButtonLink to={`/income/${r.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="income:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this income record?"
                    body="It will be removed from totals and reports. The change is kept in the audit history."
                    onConfirm={async () => {
                      await api.delete(`/api/income/${r.id}`);
                      await invalidate();
                      navigate("/income");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Transaction details">
                <DefinitionList
                  items={[
                    [
                      "Property",
                      <Link key="p" to={`/properties/${r.propertyId}`}>
                        {r.addressLine1}, {r.postcode}
                      </Link>,
                    ],
                    [
                      "Tenancy",
                      r.tenancyId ? (
                        <Link key="t" to={`/tenancies/${r.tenancyId}`}>
                          {r.tenantNames ?? "View tenancy"}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    ["Type", f.label(INCOME_TYPES, r.incomeType)],
                    ["Amount", <Money key="a" pence={r.amountPence} kind="income" />],
                    ["Date received", f.date(r.receivedDate)],
                    [
                      "Period covered",
                      r.periodFromDate ? `${f.date(r.periodFromDate)} – ${f.date(r.periodToDate)}` : "—",
                    ],
                    ["Payment method", f.label(PAYMENT_METHODS, r.paymentMethod)],
                    ["Reference", r.reference],
                    ["Description", r.description],
                    ["Notes", r.notes],
                  ]}
                />
              </Section>
              <AuditHistory entityType="income" entityId={r.id} />
            </Col>
            <Col lg={4}>
              <Attachments entityType="income" entityId={r.id} title="Evidence" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
